import {
    AppScriptParty,
    AppScriptQuestion,
    AppScriptQuestionGroup,
    AppScriptView,
    AppScriptViewQuestion,
    IsQuestionGroup,
    SetAnswerType
} from "./types";
import {StatusUpdateAnswer} from "../types/UpdatePartyObject";
import {v4} from "uuid";
import {AppAnswer, AppParty} from "../types/types";
import {Question} from "../api/types/Question";
import {patchQuestion, patchView, safeDefine} from "./helpers/viewHelper";

export type PopulateAppScriptArgs = {
    updateParty: (party: AppParty) => void,
    afterUpdate: (question: Question) => void
};

export type PopulateAppScript = (args: PopulateAppScriptArgs) => AppScriptParty | undefined;

export const populateAppScript: PopulateAppScript = ({updateParty, afterUpdate}) => {
    if (!window.party) return;
    window.copyParty = JSON.parse(JSON.stringify(window.party)) as AppScriptParty;

    window.party.presets.forEach(preset => {
        preset.findById = (id: number) => {
            const nonQuestionGroups = preset.items.filter(i => !IsQuestionGroup(i)) as AppScriptQuestion[];
            const question = nonQuestionGroups.filter(i => i.id === id);
            if (question && question.length > 0) return question[0];
            const questionGroups = preset.items.filter(i => IsQuestionGroup(i)) as AppScriptQuestionGroup[];
            const view = questionGroups.filter(ig => ig.id === id);
            if (view && view.length > 0) return view[0];
            const views = flattenBy(questionGroups, b => b.views);
            return flattenBy(views, a => [a.findById(id)])[0];
        }
        preset.findByName = (name: string) => {
            const nonQuestionGroups = preset.items.filter(i => !IsQuestionGroup(i)) as AppScriptQuestion[];
            const question = nonQuestionGroups.filter(i => i.name === name);
            if (question && question.length > 0) return question[0];
            const questionGroups = preset.items.filter(i => IsQuestionGroup(i)) as AppScriptQuestionGroup[];
            const view = questionGroups.filter(ig => ig.name === name);
            if (view && view.length > 0) return view[0];
            const views = flattenBy(questionGroups, b => b.views);
            return flattenBy(views, a => [a.findByName(name)])[0];
        }

        preset.items.forEach((item) => {
            // If item is a QuestionGroup (have views)
            if (IsQuestionGroup(item)) {
                item.findById = (id: number) => flattenBy(item.views, (view => [view.findById(id)]))[0];
                item.findByName = (name: string) => flattenBy(item.views, (view => [view.findByName(name)]))[0];
                item.hide = (state: boolean) => hide(item, state);
                item.disable = (state: boolean) => disable(item, state);

                Object.defineProperty(item as AppScriptQuestionGroup, "views", {
                    get() {
                        for (const preset of window.copyParty.presets) {
                            const found = preset.items.find(
                                i => IsQuestionGroup(i) && i.id === item.id
                            ) as AppScriptQuestionGroup;

                            if (!found) continue;

                            // Патчим ровно один раз
                            found.views.forEach(view =>
                                patchView(view, {
                                    findById: (arr, id) => findById(arr, id),
                                    findByName: (arr, name) => findByName(arr, name),
                                    clearAll: (v) => {
                                        for (const q of v.questions) clear(q);
                                        updateParty(window.party);
                                    },
                                    patchQuestion: (q) =>
                                        patchQuestion(q, {
                                            getAnswers,
                                            setAnswer,
                                            clearWithUpdateParty,
                                            hide,
                                            disable,
                                            on
                                        }),
                                })
                            );

                            // Безопасно добавляем метод delete к массиву views
                            safeDefine(found.views as any, "delete", (deletedViews: AppScriptView | AppScriptView[]) => {
                                const viewsToDelete = Array.isArray(deletedViews) ? deletedViews : [deletedViews];
                                let deletedIndexes: number[] = [];
                                for (const view of viewsToDelete) {
                                    const index = found.views.findIndex(v => v.order === view.order);
                                    if (index < 0) continue;
                                    deletedIndexes.push(index);
                                }
                                deletedIndexes = deletedIndexes.sort();
                                for (let i = deletedIndexes.length - 1; i >= 0; i--) {
                                   for (const question of found.views[i].questions) {
                                        if (question.answers.every(a => !a.text)) continue;
                                        if (Array.isArray(question.answers)) {
                                            question.answers.forEach(answer => (answer.text = ""));
                                        }
                                        afterUpdate(question);
                                        question.answers = [generateNewAnswer(question, "")];
                                    }
                                    if (found.views.length > 1) {
                                        found.views.splice(i, 1);
                                    }
                                }
                                updateParty(window.party);
                            });

                            return found.views;
                        }
                        return [];
                    },
                    configurable: true,
                    enumerable: true,
                });
            } else {
                item.getAnswers = () => getAnswers(item);
                item.setAnswer = answer => setAnswer(item, answer)
                item.clear = () => clearWithUpdateParty(item);
                item.hide = (state: boolean) => hide(item, state);
                item.disable = (state: boolean) => disable(item, state);
                item.on = (_event: "changed", callback: (e: any) => void) => on(_event, callback, item);
            }

        })
    });

    window.party.findById = (id: number) => flattenBy(window.party.presets, (preset => [preset.findById(id)]))[0];
    window.party.findByName = (name: string) => flattenBy(window.party.presets, (preset => [preset.findByName(name)]))[0];

    return window.party;

    function clear(question: AppScriptQuestion | AppScriptViewQuestion) {
        const reallyQuestion = getReallyQuestion(question);
        if (!reallyQuestion) return;
        if (Array.isArray(reallyQuestion.answers)) {
            reallyQuestion.answers.forEach(answer => {
                answer.text = '';
            });
        }
        afterUpdate(reallyQuestion);
        reallyQuestion.answers = [generateNewAnswer(reallyQuestion, "")];
    }

    function clearWithUpdateParty(question: AppScriptQuestion | AppScriptViewQuestion) {
        clear(question);
        updateParty(window.party);
    }

    function disable(question: AppScriptQuestion | AppScriptViewQuestion | AppScriptQuestionGroup, state: boolean): void {
        const reallyQuestion = IsQuestionGroup(question) ? getReallyGroup(question) : getReallyQuestion(question);
        if (!reallyQuestion) return;
        reallyQuestion.editable = !state;
        updateParty(window.party);
    }

    function hide(question: AppScriptQuestion | AppScriptViewQuestion | AppScriptQuestionGroup, state: boolean): void {
        const reallyQuestion = IsQuestionGroup(question) ? getReallyGroup(question) : getReallyQuestion(question)
        if (!reallyQuestion) return;
        reallyQuestion.hidden = state;
        updateParty(window.party);
    }

    function on(_event: "changed", callback: (e: any) => void, item: AppScriptQuestion | AppScriptViewQuestion) {
        const interval = setInterval(() => {
            const element = window.document.getElementById(`${item.id}`);
            if (!element) return;
            clearInterval(interval);
            element.addEventListener("answerUpdate", a => {
                callback(a)
            });
        }, 1000);
    }

    function setAnswer(question: AppScriptQuestion | AppScriptViewQuestion, answer: SetAnswerType): void {
        const reallyQuestion = getReallyQuestion(question);
        if (!reallyQuestion) return;
        if (!Array.isArray(question.answers) || !Object.isExtensible(reallyQuestion.answers)) {
            reallyQuestion.answers = [];
        }
        if (reallyQuestion.answers.length > 0) {
            reallyQuestion.answers = question.answers.sort(a => a.order);
        }
        if (!answer) {
            reallyQuestion.clear();
        } else if (reallyQuestion.allowMultiple && isStringArray(answer)) {
            if (reallyQuestion.answers.length > answer.length) {
                reallyQuestion.answers = reallyQuestion.answers.slice(0, answer.length);
            }
            for (let i = 0; i < answer.length; i++) {
                if (reallyQuestion.answers[i]) {
                    reallyQuestion.answers[i].text = answer[i] ?? "";
                    reallyQuestion.answers[i].order = i;
                    continue;
                }
                reallyQuestion.answers.push(generateNewAnswer(reallyQuestion, answer[i]));
            }
        } else if (isIdArray(answer)) {
            for (let i = 0; i < answer.length; i++) {
                if (reallyQuestion.answers[i]) {
                    reallyQuestion.answers[i].text = answer[i]?.id ?? "";
                    reallyQuestion.answers[i].order = i;
                    continue;
                }
                reallyQuestion.answers.push(generateNewAnswer(reallyQuestion, answer[i]?.id ?? ""));
            }
        } else if (typeof answer === "string") {
            if (reallyQuestion.answers.length > 0) {
                reallyQuestion.answers = [{...reallyQuestion.answers[0], text: answer ?? "", order: 0}];
            } else {
                reallyQuestion.answers.push(generateNewAnswer(reallyQuestion, answer));
            }
        } else if ("id" in answer) {
            if (reallyQuestion.answers.length > 0) {
                reallyQuestion.answers = [{...reallyQuestion.answers[0], text: answer?.id ?? "", order: 0}];
            } else {
                reallyQuestion.answers.push(generateNewAnswer(reallyQuestion, answer?.id ?? ""));
            }
        }
        afterUpdate(reallyQuestion);
        updateParty(window.party);
    }
}

function findByName(array: (AppScriptQuestion | AppScriptViewQuestion)[], name: string): (AppScriptQuestion | AppScriptViewQuestion) {
    return array.filter((item: AppScriptQuestion | AppScriptViewQuestion) => item.name === name)[0];
}

function findById(array: (AppScriptQuestion | AppScriptViewQuestion)[], id: number): (AppScriptQuestion | AppScriptViewQuestion) {
    return array.filter((item: AppScriptQuestion | AppScriptViewQuestion) => item.id === id)[0];
}


function getReallyQuestion(question: AppScriptQuestion | AppScriptViewQuestion) {
    const reallyQuestion = window.party.findById(question.id);
    if (IsQuestionGroup(question)) return undefined;
    return reallyQuestion as AppScriptQuestion | AppScriptViewQuestion;
}

function getReallyGroup(questionGroup: AppScriptQuestionGroup) {
    return flattenBy(window.party.presets, p => p.items).find(i => IsQuestionGroup(i) && i.id === questionGroup.id) as AppScriptQuestionGroup;
}

function getAnswers(question: AppScriptQuestion | AppScriptViewQuestion) {
    const reallyQuestion = getReallyQuestion(question);
    if (!reallyQuestion || IsQuestionGroup(reallyQuestion)) return [];
    const answers = reallyQuestion.answers;
    if (!answers || (answers.length === 1 && !answers[0].text)) return [];
    return answers;
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === "string");
}

function isIdArray(value: unknown): value is { id: string }[] {
    return Array.isArray(value) && value.every(item => "id" in item);
}

function flattenBy<T, U>(array: T[], selector: (item: T) => U[]): U[] {
    return array.reduce<U[]>((acc, item) => acc.concat(selector(item)), []);
}

function generateNewAnswer(question: AppScriptQuestion | AppScriptViewQuestion, answer: string): AppAnswer {
    const resolvedGroupId = getGroupIdForQuestion(question);
    return {
        text: answer,
        id: v4(),
        questionId: question.id,
        groupId: resolvedGroupId,
        order: 0,
        groupOrder: 0,
        attachments: [],
        status: StatusUpdateAnswer.NONE,
        errorText: "",
    };
}

function getGroupIdForQuestion(question: AppScriptQuestion | AppScriptViewQuestion): number | undefined {
    for (const preset of window.party.presets) {
        for (const item of preset.items) {
            if (IsQuestionGroup(item)) {
                for (const view of item.views) {
                    if (view.questions?.some(q => q.id === question.id)) {
                        return item.id;
                    }
                }
            }
        }
    }
    return undefined;
}